import { useTheme, INavLinkGroup, INavStyles, mergeStyles, Nav } from "@fluentui/react";
import * as React from "react";
import { useEffect, useState } from "react";
import { Utils } from "../Utils";
import { RouteComponentProps } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks";
import { loadTestCategories } from "../features/testSelection/testSelectionSlice";
import { toggleNav } from "../actions/globalConfigActions";

/**
 * The left navigation bar component
 *
 * @param props - the Component props
 * @returns - the component
 */
export const RcaNav: React.FunctionComponent<RouteComponentProps> = (props) => {
  const dispatch = useAppDispatch();
  const language = useAppSelector((state) => state.globalConfig.language);
  const testCategories = useAppSelector((state) => state.testSelection.testCategories);
  const showNav = useAppSelector((state) => state.globalConfig.showNav);
  const selectedTestCategory = getCategoryFromPathName(props.location.pathname);

  useEffect(() => {
    void dispatch(loadTestCategories(language));
  }, [dispatch, language]);

  // The goal of all this code below is to adjust the height
  // of the menu in "mobile" mode which should be 100% minus the top vertical offset
  // We can only get the offsetTop of the element via script and we need
  // to re-render any time that changes.
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState(50);

  // Handles window resizes
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current && wrapperRef.current.offsetTop > 0) {
        setOffsetTop(wrapperRef.current.offsetTop);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Updates the offsetTop when the NAV is rendered or toggled
  useEffect(() => {
    if (wrapperRef.current && wrapperRef.current.offsetTop > 0) {
      setOffsetTop(wrapperRef.current.offsetTop);
    }
  }, [wrapperRef, showNav]);

  const connectivityTests: INavLinkGroup = {
    name: ClientStrings.ConnectivityTestsNavGroupHeader,
    expandAriaLabel: ClientStrings.ConnectivityTestsNavGroupHeader,
    collapseAriaLabel: ClientStrings.ConnectivityTestsNavGroupHeader,
    links: testCategories.map((tc) => ({
      name: tc.name,
      url: `/tests/${tc.id}`,
      key: tc.id,
      icon: tc.icon ? tc.icon : "",
      title: tc.tooltip ? tc.tooltip : undefined,
      forceAnchor: true,
      onClick: (e) => {
        e?.preventDefault();

        // On mobile, close the navigation overlay before moving focus into visible page content.
        if (selectedTestCategory === tc.id) {
          if (showNav) {
            dispatch(toggleNav());
            props.history.replace(`/tests/${tc.id}`, { focusCategoryHeading: true });
          }

          return;
        }

        if (showNav) {
          dispatch(toggleNav());
        }

        props.history.push(`/tests/${tc.id}`, { focusCategoryHeading: true });
      },
    })),
  };

  const otherTools: INavLinkGroup = {
    name: ClientStrings.OtherToolsNavGroupHeader,
    expandAriaLabel: ClientStrings.OtherToolsNavGroupHeader,
    collapseAriaLabel: ClientStrings.OtherToolsNavGroupHeader,
    links: [
      {
        name: WebsiteStrings.TabTitleMHA,
        url: "https://aka.ms/mha",
        target: "_blank",
        key: "mha",
        icon: "Mail",
        title: ClientStrings.MhaNavItemTooltipText,
      },
      {
        name: ClientStrings.M365NetworkNavItem,
        url: "https://aka.ms/AAhrv8k",
        target: "_blank",
        key: "m365network",
        icon: "Diagnostic",
        title: ClientStrings.M365NetworkTooltip,
      },
    ],
  };

  const { semanticColors } = useTheme();

  const minWidth = 208;

  const wrapperStyle = mergeStyles({
    overflowY: "auto",
    [Utils.mobileQuery]: {
      display: showNav ? "block" : "none",
      position: "absolute",
      zIndex: 999,
      height: `calc(100% - ${offsetTop}px)`,
    },
  });

  const styles: Partial<INavStyles> = {
    root: {
      minWidth: minWidth,
      borderStyle: "none",
      backgroundColor: semanticColors.bodyBackground,
      ".ms-Button": {
        paddingLeft: 15,
        paddingRight: 15,
      },
    },
    group: {
      ".ms-Nav-chevronButton": { borderWidth: 0, marginTop: 0, marginBottom: 0, fontSize: 14 },
    },
    groupContent: {
      marginTop: 0,
      marginBottom: 0,
    },
    link: {
      color: semanticColors.actionLink, // Link color
      ".ms-Button-icon": { color: semanticColors.actionLink },
      ":hover, &:hover .ms-Button-icon": {
        color: semanticColors.actionLinkHovered,
      },
      ":active, &:active .ms-Button-icon": {
        color: semanticColors.actionLinkHovered,
      },
      "::after": { borderLeftWidth: 5 },
    },
  };

  return (
    <div ref={wrapperRef} className={wrapperStyle}>
      <Nav selectedKey={selectedTestCategory ?? ""} styles={styles} groups={[connectivityTests, otherTools]} />
    </div>
  );
};

/**
 * Gets the test category from browser location
 *
 * @param path - The current path name from the browser
 * @returns The path name
 */
const getCategoryFromPathName = (path: string): string => {
  const match = /\/tests\/(.+)/.exec(path);
  if (match) {
    return match[1];
  }

  return "";
};

export default RcaNav;
